import React, { useEffect, useState } from "react";
import icon1 from "../../assets/images/admin/dashboard/icon1.svg";
import icon2 from "../../assets/images/admin/dashboard/icon2.svg";
import icon3 from "../../assets/images/admin/dashboard/icon3.svg";
import icon4 from "../../assets/images/admin/dashboard/icon4.svg";
import "bootstrap/dist/css/bootstrap.min.css";

import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import Swal from "sweetalert2";
import { apiUrl } from "../../config/Config";

const emptyDashboard = {
  summary: {
    today_sales: 0,
    today_orders: 0,
    products_sold_today: 0,
    new_customers_today: 0,
    total_orders: 0,
    total_customers: 0,
    total_products: 0,
    pending_questions: 0,
    pending_refunds: 0,
  },
  recent_orders: [],
  recent_messages: [],
};

const Dashboard = () => {
  const navigate = useNavigate();
  const userdata = useSelector((store) => store.userReducer.userdata);
  const [dashboardData, setDashboardData] = useState(emptyDashboard);
  const [loading, setLoading] = useState(true);

  const headers = {
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + userdata?.token,
    },
  };

  const formatCurrency = (amount) => `₹ ${Number(amount || 0).toFixed(2)}`;

  const formatDateTime = (dateValue) => {
    if (!dateValue) return "NA";
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return dateValue;
    return new Intl.DateTimeFormat("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  const getStatusClass = (status) => {
    if (["paid", "completed", "approved", "answered"].includes(status)) {
      return "success";
    }
    if (["failed", "rejected", "payment_failed"].includes(status)) {
      return "danger";
    }
    if (["pending", "created", "requested", "under_review"].includes(status)) {
      return "warning";
    }
    return "";
  };

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const res = await axios.post(apiUrl + "getadmindashboard", {}, headers);
      if (res.data.status === "success") {
        setDashboardData({
          ...emptyDashboard,
          ...res.data.data,
          summary: {
            ...emptyDashboard.summary,
            ...(res.data.data?.summary || {}),
          },
        });
      } else {
        throw new Error(res.data.message || "Unable to fetch dashboard");
      }
    } catch (error) {
      Swal.fire({
        text: error?.response?.data?.message || error.message || "Unable to fetch dashboard.",
        icon: "error",
        confirmButtonColor: "rgb(59, 151, 196)",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userdata && Object.keys(userdata).length > 0) {
      fetchDashboard();
    } else {
      navigate("/admin/login");
    }
  }, []);

  const summary = dashboardData.summary || emptyDashboard.summary;
  const statCards = [
    {
      icon: icon1,
      value: formatCurrency(summary.today_sales),
      label: "Today's Sales",
      note: `${summary.total_orders || 0} total orders`,
      bg: "#FFE2E5",
    },
    {
      icon: icon2,
      value: summary.today_orders || 0,
      label: "Today's Orders",
      note: `${summary.pending_refunds || 0} refund requests pending`,
      bg: "#FFF4DE",
    },
    {
      icon: icon3,
      value: summary.products_sold_today || 0,
      label: "Product Sold",
      note: `${summary.total_products || 0} products in catalog`,
      bg: "#DCFCE7",
    },
    {
      icon: icon4,
      value: summary.new_customers_today || 0,
      label: "New Customers",
      note: `${summary.total_customers || 0} total customers`,
      bg: "#F3E8FF",
    },
  ];

  return (
    <div>
      <div className="dashboardrow">
        <div className="dashboardbox">
          <div className="dashboardboxtitlerow">
            <div>
              <p className="dashboardboxtitle">Today&apos;s Sales</p>
              <p className="dashboardboxsubtitle">
                {loading ? "Loading live summary..." : "Live sales summary"}
              </p>
            </div>
          </div>

          <div className="dashboardsummaryrow row">
            {statCards.map((card) => (
              <div className="col-md-3" key={card.label}>
                <div className="summaryboxrow" style={{ backgroundColor: card.bg }}>
                  <img src={card.icon} className="summaryicon1" alt="" />
                  <p className="iconbox1count">{card.value}</p>
                  <p className="iconbox1subtext">{card.label}</p>
                  <p className="iconbox1subtext1">{card.note}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="dashboardbox visitorinsightbox" style={{ paddingBottom: 0 }}>
          <div className="dashboardboxtitlerow">
            <div>
              <p className="dashboardboxtitle">Recent Customer Requests</p>
              <p className="dashboardboxsubtitle">
                {summary.pending_questions || 0} questions pending response
              </p>
            </div>
          </div>
          <div className="vistorchartdiv">
            <table className="table dashboardtable">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Request</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {dashboardData.recent_messages.length ? (
                  dashboardData.recent_messages.map((item) => (
                    <tr key={`${item.type}-${item.id}`}>
                      <td>
                        <button
                          className="admin-link-cell"
                          onClick={() => item.customer_id && navigate(`/admin/customer-details/${item.customer_id}`)}
                        >
                          {item.customer_name || "Customer"}
                        </button>
                      </td>
                      <td>{item.title}</td>
                      <td>
                        <div className={`dashboard-delivered-btn ${getStatusClass(item.status)}`}>
                          <p>{item.status || "open"}</p>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" className="dashboard-empty-cell">
                      No recent customer requests.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="dashboardrow mt-4">
        <div className="dashboardbox visitorinsightbox" style={{ paddingBottom: 0, width: "71%" }}>
          <div className="dashboardboxtitlerow">
            <div>
              <p className="dashboardboxtitle">Recent Orders</p>
              <p className="dashboardboxsubtitle">Latest customer checkout activity</p>
            </div>
          </div>
          <div className="vistorchartdiv">
            <table className="table dashboardtable">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Customer</th>
                  <th>Date - Time</th>
                  <th>Items</th>
                  <th>Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {dashboardData.recent_orders.length ? (
                  dashboardData.recent_orders.map((order) => (
                    <tr key={order.id}>
                      <td>{order.order_number || `Order #${order.id}`}</td>
                      <td>
                        <button
                          className="admin-link-cell"
                          onClick={() => order.customer_id && navigate(`/admin/customer-details/${order.customer_id}`)}
                        >
                          {order.customer_name || "Customer"}
                        </button>
                      </td>
                      <td>{formatDateTime(order.created_at)}</td>
                      <td>{order.items_count || 0}</td>
                      <td>{formatCurrency(order.amount)}</td>
                      <td>
                        <div className={`dashboard-delivered-btn ${getStatusClass(order.payment_status || order.status)}`}>
                          <p>{order.payment_status || order.status || "pending"}</p>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="dashboard-empty-cell">
                      No orders found yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="dashboardbox visitorinsightbox" style={{ paddingBottom: 0, width: "27%" }}>
          <div className="dashboardboxtitlerow">
            <div>
              <p className="dashboardboxtitle">Store Snapshot</p>
              <p className="dashboardboxsubtitle">Current live totals</p>
            </div>
          </div>
          <div className="dashboard-snapshot-list">
            <div>
              <span>Total Customers</span>
              <strong>{summary.total_customers || 0}</strong>
            </div>
            <div>
              <span>Total Products</span>
              <strong>{summary.total_products || 0}</strong>
            </div>
            <div>
              <span>Total Orders</span>
              <strong>{summary.total_orders || 0}</strong>
            </div>
            <div>
              <span>Pending Refunds</span>
              <strong>{summary.pending_refunds || 0}</strong>
            </div>
            <div>
              <span>Pending Questions</span>
              <strong>{summary.pending_questions || 0}</strong>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
